import React from 'react';
import { View, ActivityIndicator, FlatList, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { strings } from '@/constants/residents/strings';
import { NotificationRow } from './NotificationRow';
import { useNotifications, NotificationItem } from '@/hooks/common/useNotifications';

type Props = {
  data: NotificationItem[] | undefined;
  isLoading: boolean;
  emptyText?: string;
  renderRight?: (item: NotificationItem) => React.ReactNode;
  onPressItem: (item: NotificationItem) => void;
  showDateSmall?: boolean;
  dateFor?: (item: NotificationItem) => string;
  selectedId?: number | null;
  rowCompact?: boolean;
  rowTitleStyleOverride?: any;
  rowRightWidth?: number;
  showSeparators?: boolean;
  rowVariant?: 'popover' | 'screen';
};

export const NotificationList: React.FC<Props> = ({ data, isLoading, emptyText, renderRight, onPressItem, showDateSmall, dateFor, selectedId, rowCompact, rowTitleStyleOverride, rowRightWidth, showSeparators = true, rowVariant }) => {
  const { colors } = useThemeColors();
  const { isUnread, formatDateMMMdd, formatDateMMMddyyyy } = useNotifications();
  const keyExtractor = React.useCallback((item: NotificationItem) => String(item.id), []);
  const renderItem = React.useCallback(({ item }: { item: NotificationItem }) => (
    <NotificationRow
      item={item}
      onPress={() => onPressItem(item)}
      isSelected={selectedId === item.id}
      showDateSmall={!!showDateSmall}
      rightAccessory={renderRight ? renderRight(item) : undefined}
      unread={isUnread(item)}
      dateText={dateFor ? dateFor(item) : (showDateSmall ? formatDateMMMddyyyy(item.createdAt) : formatDateMMMdd(item.createdAt))}
      compact={rowVariant ? rowVariant === 'popover' : !!rowCompact}
      titleStyleOverride={rowVariant ? (rowVariant === 'popover' ? { ...REDESIGN_TYPOGRAPHY.cardTitle, fontWeight: '700', color: colors.text.primary } : undefined) : rowTitleStyleOverride}
      rightWidth={rowVariant ? (rowVariant === 'popover' ? 52 : undefined) : rowRightWidth}
    />
  ), [onPressItem, selectedId, isUnread, renderRight, showDateSmall, dateFor, rowVariant, rowCompact, rowTitleStyleOverride, rowRightWidth, formatDateMMMdd, formatDateMMMddyyyy, colors.text.primary]);

  if (!isLoading && Array.isArray(data) && data.length === 0) {
    return <Text style={[styles.emptyText, { color: colors.text.subtle }]}>{emptyText || strings.notifications.desktop.empty}</Text>;
  }

  return (
    <FlatList
      data={data || []}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
      ItemSeparatorComponent={showSeparators ? (() => <View style={[styles.separator, { backgroundColor: colors.separator }]} />) : undefined}
      contentContainerStyle={styles.listContent}
      ListFooterComponent={isLoading ? (
        <ActivityIndicator style={styles.loader} size="large" color={colors.text.muted} />
      ) : null}
      showsVerticalScrollIndicator={false}
      removeClippedSubviews={true}
      windowSize={7}
      initialNumToRender={10}
      maxToRenderPerBatch={10}
    />
  );
};

const styles = StyleSheet.create({
  emptyText: {
    ...REDESIGN_TYPOGRAPHY.body,
    textAlign: 'center',
    marginTop: 48,
  },
  listContent: {
    paddingVertical: 8,
  },
  separator: {
    height: 1,
    marginHorizontal: 16,
  },
  loader: {
    marginTop: 24,
  },
});

export default NotificationList;
